import { useState } from 'react';
import { ApiError } from '../../lib/api';
import { fmtDateTime } from '../../lib/format';
import { usePublicUnit, useUnitDetail, useAddRepair, useDeleteRepair, UNIT_STATUS_META } from './api';

function tokenFromPath(): string {
  const m = window.location.pathname.match(/\/kus\/([^/?#]+)/);
  return m ? decodeURIComponent(m[1]) : '';
}

export default function UnitPage() {
  const token = tokenFromPath();
  const pub = usePublicUnit(token);
  const detail = useUnitDetail(token); // 200 jen po přihlášení
  const add = useAddRepair(token);
  const delRep = useDeleteRepair(token);
  const [desc, setDesc] = useState('');
  const [by, setBy] = useState('');
  const [cost, setCost] = useState('');

  const loggedIn = detail.isSuccess;
  const input = 'h-10 w-full rounded-md border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-3 text-sm';

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (desc.trim().length < 1) return;
    add.mutate(
      { description: desc.trim(), performedBy: by.trim() || undefined, cost: cost.trim() ? Number(cost.replace(',', '.')) : undefined },
      { onSuccess: () => { setDesc(''); setBy(''); setCost(''); } },
    );
  };

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950 text-zinc-900 dark:text-zinc-100">
      <div className="mx-auto max-w-2xl px-4 py-10">
        {pub.isLoading && <p className="text-sm text-zinc-500">Načítám…</p>}
        {pub.error && (
          <div className="rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-10 text-center">
            <h1 className="text-lg font-semibold mb-1">Kus nenalezen</h1>
            <p className="text-sm text-zinc-500">{(pub.error as ApiError).message}</p>
          </div>
        )}

        {pub.data && (
          <>
            <div className="rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-6">
              <p className="text-xs uppercase tracking-wide text-zinc-400">{pub.data.studioName}</p>
              <div className="mt-1 flex items-center gap-3">
                <h1 className="text-2xl font-bold tracking-tight">{pub.data.name}</h1>
                <span className={`rounded px-2 py-0.5 text-xs ${UNIT_STATUS_META[pub.data.status].badge}`}>{UNIT_STATUS_META[pub.data.status].label}</span>
              </div>
              <dl className="mt-4 grid grid-cols-2 gap-y-2 text-sm sm:grid-cols-4">
                <div><dt className="text-xs text-zinc-400">Kus</dt><dd className="font-mono font-medium">{pub.data.serial} / {pub.data.total}</dd></div>
                <div><dt className="text-xs text-zinc-400">Verze</dt><dd>{pub.data.version ?? '—'}</dd></div>
                <div><dt className="text-xs text-zinc-400">Vyrobeno</dt><dd>{pub.data.monthYear}</dd></div>
                <div><dt className="text-xs text-zinc-400">Výrobce</dt><dd>{pub.data.studioName}</dd></div>
              </dl>
              {(pub.data.studioWeb || pub.data.studioEmail) && (
                <p className="mt-4 border-t border-zinc-100 dark:border-zinc-800 pt-3 text-xs text-zinc-400">
                  {pub.data.studioWeb && <a href={pub.data.studioWeb.startsWith('http') ? pub.data.studioWeb : `https://${pub.data.studioWeb}`} target="_blank" rel="noreferrer" className="hover:underline">{pub.data.studioWeb}</a>}
                  {pub.data.studioWeb && pub.data.studioEmail && ' · '}
                  {pub.data.studioEmail && <a href={`mailto:${pub.data.studioEmail}`} className="hover:underline">{pub.data.studioEmail}</a>}
                </p>
              )}
            </div>

            {/* Historie oprav — jen po přihlášení */}
            {loggedIn && detail.data ? (
              <div className="mt-6 rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-6">
                <h2 className="mb-3 font-semibold">Historie oprav <span className="text-sm font-normal text-zinc-500">({detail.data.repairs.length})</span></h2>

                <form onSubmit={submit} className="mb-4 space-y-2">
                  <textarea className="w-full rounded-md border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-3 py-2 text-sm" rows={2} placeholder="Co se opravovalo…" value={desc} onChange={(e) => setDesc(e.target.value)} />
                  <div className="grid grid-cols-[1fr_8rem_auto] gap-2">
                    <input className={input} placeholder="Kdo (volitelné)" value={by} onChange={(e) => setBy(e.target.value)} />
                    <input className={input} inputMode="decimal" placeholder="Cena" value={cost} onChange={(e) => setCost(e.target.value)} />
                    <button type="submit" disabled={add.isPending || !desc.trim()} className="h-10 rounded-md bg-zinc-900 px-4 text-sm font-medium text-white hover:bg-zinc-800 disabled:opacity-50 dark:bg-zinc-100 dark:text-zinc-900">{add.isPending ? '…' : 'Zapsat'}</button>
                  </div>
                  {add.error && <p className="text-xs text-red-600 dark:text-red-400">{(add.error as ApiError).message}</p>}
                </form>

                {detail.data.repairs.length === 0 ? (
                  <p className="text-sm text-zinc-400">Zatím žádná oprava.</p>
                ) : (
                  <ul className="divide-y divide-zinc-100 dark:divide-zinc-800">
                    {detail.data.repairs.map((r) => (
                      <li key={r.id} className="py-2.5 text-sm">
                        <div className="flex items-start justify-between gap-2">
                          <span className="whitespace-pre-line">{r.description}</span>
                          <button onClick={() => delRep.mutate(r.id)} className="shrink-0 text-zinc-400 hover:text-red-600" aria-label="Smazat" title="Smazat">×</button>
                        </div>
                        <div className="mt-0.5 text-xs text-zinc-400">
                          {fmtDateTime(r.createdAt)}{r.performedBy ? ` · ${r.performedBy}` : ''}{r.cost ? ` · ${Number(r.cost).toLocaleString('cs-CZ')} Kč` : ''}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ) : (
              <p className="mt-6 text-center text-xs text-zinc-400">
                Historie oprav je viditelná po <a href="/" className="underline">přihlášení</a> do Studio OS.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
